import { Link } from "react-router-dom";
import { ArrowRight, Shield, Factory, Landmark, Briefcase, Target, Globe, Crosshair } from "lucide-react";
import { motion } from "framer-motion";
import {
  FadeIn, FadeInX, StaggerContainer, StaggerItem,
  RevealLine, GoldBar, ImageReveal, PageTransition,
  HoverCard, ParallaxBg,
} from "@/components/site/Animations";
import { SectionHeader } from "@/components/site/SectionHeader";
import { WorkflowRail } from "@/components/site/WorkflowRail";
import { ContactBanner } from "@/components/site/ContactBanner";

const HERO_IMAGE = "https://images.unsplash.com/photo-1769144256227-5185141c3aca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwaW5mcmFzdHJ1Y3R1cmUlMjBwb3J0JTIwc2hpcHBpbmclMjBjb250YWluZXJzJTIwYWVyaWFsfGVufDB8fHx8MTc3MzM0NzQ0MXww&ixlib=rb-4.1.0&q=85";

const SECTORS = [
  {
    icon: Crosshair,
    title: "Defense Procurement",
    description: "Secure acquisition and deployment of mission-critical defense systems. Program alignment with government procurement frameworks and operational readiness.",
  },
  {
    icon: Shield,
    title: "Energy & Emerging Applications",
    description: "Resilience and grid-adjacent programs, alternative fuel pathways, and industrial-scale energy output for mission-critical environments.",
  },
  {
    icon: Factory,
    title: "Critical Materials & Supply Chains",
    description: "Construction commodities to advanced, tech-enabled materials. Restricted and sensitive sourcing across regulated supply environments.",
  },
  {
    icon: Landmark,
    title: "Industrial Infrastructure & Manufacturing",
    description: "Prefabrication, modular systems, rapid-deployment infrastructure, and manufacturing-enabled delivery for scalable programs.",
  },
];

const FEATURED_ITEMS = [
  {
    title: "Ketzal 7HSR Interceptor-Defender Drone",
    description: "AI-driven, fully human supervised interceptor-defender drone system. Compact quad-rotor designed to chase and neutralize enemy strike drones at speeds up to 400 km/h. Exclusive to Cortes Industries.",
    highlight: true,
  },
  {
    title: "Energy Infrastructure Systems",
    description: "Grid-adjacent and alternative fuel procurement support for resilience programs and industrial-scale energy output.",
  },
  {
    title: "Advanced Manufacturing Solutions",
    description: "Prefabrication, modular systems, and rapid-deployment manufacturing capabilities for time-sensitive programs.",
  },
  {
    title: "Strategic Sourcing Programs",
    description: "End-to-end procurement strategy development including partner architecture, qualification, and delivery assurance.",
  },
];

const PROOF_POINTS = [
  { label: "Sectors Served", value: "4 Core", icon: Target },
  { label: "Operating Regions", value: "5 Global", icon: Globe },
  { label: "Approach", value: "Operator-Led", icon: Briefcase },
];

export default function HomePage() {
  return (
    <PageTransition>
      <div data-testid="home-page">
        {/* ====== HERO ====== */}
        <section className="relative bg-[hsl(218,60%,10%)] text-white overflow-hidden">
          {/* Parallax Background */}
          <ParallaxBg className="absolute inset-0" speed={0.08}>
            <img
              src={HERO_IMAGE}
              alt="Industrial infrastructure"
              className="w-full h-[120%] object-cover opacity-25"
            />
          </ParallaxBg>
          <div className="absolute inset-0 bg-gradient-to-r from-[hsl(218,60%,10%)] via-[hsl(218,60%,10%)]/85 to-transparent" />

          <div className="relative max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-8 py-20 sm:py-24 lg:py-32">
            <div className="max-w-2xl">
              <motion.div
                className="w-12 h-[2px] bg-[hsl(41,47%,61%)] mb-6"
                initial={{ width: 0 }}
                animate={{ width: 48 }}
                transition={{ duration: 0.8, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
              />
              <motion.h1
                className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08] mb-5"
                style={{ fontFamily: 'var(--font-display)' }}
                data-testid="home-hero-heading"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
              >
                Cortes Industries
              </motion.h1>
              <motion.p
                className="text-base sm:text-lg lg:text-xl font-medium text-white/85 mb-3 leading-relaxed"
                style={{ fontFamily: 'var(--font-display)' }}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.55, ease: [0.25, 0.1, 0.25, 1] }}
              >
                Procurement and Contracting Execution for Outcome-Driven Programs
              </motion.p>
              <motion.p
                className="text-sm sm:text-base text-white/60 mb-8 max-w-lg leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
              >
                An operator-led industrial platform built for government-facing and mission-critical procurement where timelines are fixed, requirements evolve, and supply chains carry geopolitical and regulatory weight.
              </motion.p>
              <motion.div
                className="flex flex-col sm:flex-row gap-3"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.85, ease: [0.25, 0.1, 0.25, 1] }}
              >
                <Link
                  to="/contact"
                  data-testid="home-hero-primary-cta"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold bg-[hsl(41,47%,61%)] text-[hsl(218,60%,10%)] rounded-md hover:bg-[hsl(41,47%,66%)] transition-all btn-press"
                >
                  Contact Cortes Industries
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/capabilities"
                  data-testid="home-hero-secondary-cta"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold border border-white/25 text-white rounded-md hover:bg-white/10 transition-all btn-press"
                >
                  View Capabilities
                </Link>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ====== PROOF POINTS STRIP ====== */}
        <section className="bg-white border-b border-border">
          <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-8">
            <StaggerContainer stagger={0.15} className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border">
              {PROOF_POINTS.map((point, idx) => (
                <StaggerItem key={point.label}>
                  <div className={`flex items-center gap-4 py-6 sm:py-8 ${idx === 0 ? "sm:pr-6" : "sm:pl-10 sm:pr-6"}`}>
                    <point.icon className="w-5 h-5 text-[hsl(41,47%,61%)] shrink-0" />
                    <div>
                      <p className="text-lg font-bold text-foreground" style={{ fontFamily: 'var(--font-display)' }}>
                        {point.value}
                      </p>
                      <p className="text-xs text-muted-foreground">{point.label}</p>
                    </div>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* ====== WHAT CORTES IS ====== */}
        <section className="py-14 sm:py-16 lg:py-20 bg-white" data-testid="what-cortes-is">
          <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
              <FadeInX className="lg:col-span-5" x={-40}>
                <SectionHeader
                  eyebrow="About"
                  title="What Cortes Is"
                  description="An operator-led industrial platform focused on procurement execution and supply chain architecture for complex, mission-critical environments."
                />
              </FadeInX>
              <FadeIn className="lg:col-span-7" delay={0.2}>
                <div className="space-y-5">
                  <p className="text-sm sm:text-base leading-relaxed text-muted-foreground">
                    Cortes Industries supports complex procurement environments and mission-critical programs. We take the best of the legacy playbook — high-trust relationships, cross-border fluency, big-picture pattern recognition — and translate it into a modern, global build platform.
                  </p>
                  <p className="text-sm sm:text-base leading-relaxed text-muted-foreground">
                    We take ownership of the complex, risk-exposed pieces — partner selection, delivery assurance, and stakeholder coordination — so our clients can focus on mission outcomes.
                  </p>
                  <StaggerContainer stagger={0.12} delay={0.3} className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                    {[
                      { title: "Partner Selection", desc: "Qualify and structure for accountability" },
                      { title: "Delivery Assurance", desc: "Milestones, schedules, and contingencies that hold" },
                      { title: "Stakeholder Coordination", desc: "Keep agencies, primes, and vendors aligned" },
                    ].map((item) => (
                      <StaggerItem key={item.title}>
                        <div className="border-l-2 border-[hsl(41,47%,61%)] pl-4">
                          <p className="text-sm font-semibold text-foreground" style={{ fontFamily: 'var(--font-display)' }}>{item.title}</p>
                          <p className="text-xs text-muted-foreground mt-1">{item.desc}</p>
                        </div>
                      </StaggerItem>
                    ))}
                  </StaggerContainer>
                </div>
              </FadeIn>
            </div>
          </div>
        </section>

        <RevealLine />

        {/* ====== EXECUTION FRAMEWORK ====== */}
        <section className="py-14 sm:py-16 lg:py-20 bg-secondary/40" data-testid="execution-framework">
          <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-8">
            <FadeIn className="mb-10">
              <SectionHeader
                eyebrow="Operating Model"
                title="Execution Framework"
                description="Engagements structured around operational impact — from initial capture through delivery and stakeholder coordination."
                align="center"
              />
            </FadeIn>
            <WorkflowRail />
          </div>
        </section>

        {/* ====== SECTOR FOCUS PREVIEW ====== */}
        <section className="py-14 sm:py-16 lg:py-20 bg-white" data-testid="sector-preview">
          <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-8">
            <FadeIn>
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
                <SectionHeader
                  eyebrow="Where We Operate"
                  title="Sector Focus"
                  description="Strategic sourcing landscapes in the defense sector and adjacent industries."
                />
                <Link
                  to="/sectors"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(41,47%,61%)] hover:text-[hsl(41,47%,51%)] transition-colors gold-underline shrink-0"
                >
                  View All Sectors <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </FadeIn>
            <StaggerContainer stagger={0.12} className="grid grid-cols-1 md:grid-cols-3 gap-5 auto-rows-fr">
              {SECTORS.map((sector) => (
                <StaggerItem key={sector.title} className="flex">
                  <Link
                    to="/sectors"
                    className="group border border-border rounded-lg p-6 bg-white card-hover block flex-1 flex flex-col"
                    data-testid={`sector-card-${sector.title.split(' ')[0].toLowerCase()}`}
                  >
                    <sector.icon className="w-6 h-6 text-[hsl(41,47%,61%)] mb-4" />
                    <h3
                      className="text-lg font-semibold tracking-tight text-foreground mb-2"
                      style={{ fontFamily: 'var(--font-display)' }}
                    >
                      {sector.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {sector.description}
                    </p>
                    <span className="inline-flex items-center gap-1 mt-4 text-xs font-medium text-[hsl(41,47%,61%)] group-hover:text-[hsl(41,47%,51%)] transition-colors">
                      Learn More <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
                    </span>
                  </Link>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>

        <RevealLine />

        {/* ====== FEATURED PRODUCTS & SERVICES PREVIEW ====== */}
        <section className="py-14 sm:py-16 lg:py-20 bg-secondary/40" data-testid="featured-preview">
          <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-8">
            <FadeIn>
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
                <SectionHeader
                  eyebrow="Offerings"
                  title="Featured Products & Services"
                  description="High-interest technologies, materials, and procurement opportunities connected to the Cortes Industries network."
                />
                <Link
                  to="/featured"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(41,47%,61%)] hover:text-[hsl(41,47%,51%)] transition-colors gold-underline shrink-0"
                >
                  View All <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </FadeIn>
            <StaggerContainer stagger={0.1} className="grid grid-cols-1 sm:grid-cols-2 gap-5 auto-rows-fr">
              {FEATURED_ITEMS.map((item) => (
                <StaggerItem key={item.title} className="flex">
                  <div
                    className={`border rounded-lg p-6 bg-white card-hover flex flex-col flex-1 ${
                      item.highlight
                        ? "border-l-4 border-l-[hsl(41,47%,61%)] border-border"
                        : "border-border"
                    }`}
                  >
                    {item.highlight && (
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-2 h-2 rounded-full bg-green-500" />
                        <span className="text-xs font-semibold tracking-wide uppercase text-[hsl(41,47%,61%)]" style={{ fontFamily: 'var(--font-display)' }}>
                          Featured Product
                        </span>
                      </div>
                    )}
                    <h3
                      className="text-base font-semibold tracking-tight text-foreground mb-2"
                      style={{ fontFamily: 'var(--font-display)' }}
                    >
                      {item.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* ====== LEADERSHIP PREVIEW ====== */}
        <section className="py-14 sm:py-16 lg:py-20 bg-white" data-testid="leadership-preview">
          <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <ImageReveal className="lg:col-span-4">
                <div className="aspect-[3/4] max-w-[320px] rounded-lg overflow-hidden bg-secondary img-zoom">
                  <img
                    src="https://customer-assets.emergentagent.com/job_cortes-execution/artifacts/2ru3rsfx_image.png"
                    alt="Doug Quezada — Principal, Cortes Industries"
                    className="w-full h-full object-cover"
                  />
                </div>
              </ImageReveal>
              <FadeIn className="lg:col-span-8" delay={0.2}>
                <SectionHeader
                  eyebrow="Leadership"
                  title="Doug Quezada"
                  className="mb-4"
                />
                <p className="text-xs font-semibold tracking-[0.1em] uppercase text-muted-foreground mb-4" style={{ fontFamily: 'var(--font-display)' }}>
                  Founder & Principal
                </p>
                <p className="text-sm sm:text-base leading-relaxed text-muted-foreground mb-4">
                  Doug Quezada and associates bring over two decades of domestic and international experience in operations, procurement oversight, and multichannel distribution across high-stakes technology and commodity sectors.
                </p>
                <p className="text-sm sm:text-base leading-relaxed text-muted-foreground mb-6">
                  Quezada and global partners have negotiated cross-border supply and technology agreements and directed teams through complex procurement and contracting cycles across the U.S., Latin America, CEE, MENA, and ESEA.
                </p>
                <Link
                  to="/leadership"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(41,47%,61%)] hover:text-[hsl(41,47%,51%)] transition-colors gold-underline"
                >
                  Full Biography <ArrowRight className="w-4 h-4 transition-transform hover:translate-x-1" />
                </Link>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* ====== CONTACT BANNER ====== */}
        <ContactBanner />
      </div>
    </PageTransition>
  );
}
